import * as moonPaySdk from '@moonpay/platform-sdk-react-native'

import { logger } from '../../logging/logger'

declare const __DEV__: boolean | undefined

/**
 * The shape the tap needs from MoonPay's `WebViewTransport`. Declared
 * structurally so the wrap can be exercised against a fake, without loading
 * the SDK's react-native-webview dependency under vitest.
 */
export interface TraceableTransport {
  url?: string
  handleWebViewMessage(data: string): void
  sendMessage(message: unknown): void
}

interface TraceEnvelope {
  kind: string
  at: number
  payload: unknown
}

// `Symbol()`, not `Symbol.for()` — only the former infers `unique symbol`,
// which is what a computed key in a type literal requires.
const TRACED = Symbol('funkit.moonpayTraced')

/**
 * Frame payloads carry session tokens and payment state, so no build that could
 * reach a user may ship them: a debug tag alone is not enough, the bundle has to
 * be a development one too.
 */
function tracingEnabled(): boolean {
  const isDevBuild = typeof __DEV__ !== 'undefined' && !!__DEV__
  return isDevBuild && logger.getDebugTag() !== null
}

function traceEnvelope(data: string): TraceEnvelope | undefined {
  if (!data.includes('__funTrace')) {
    return undefined
  }
  try {
    const parsed: unknown = JSON.parse(data)
    if (typeof parsed !== 'object' || parsed === null) {
      return undefined
    }
    const envelope = (parsed as { __funTrace?: unknown }).__funTrace
    return typeof envelope === 'object' && envelope !== null
      ? (envelope as TraceEnvelope)
      : undefined
  } catch {
    return undefined
  }
}

/**
 * Wrap a transport prototype's two message paths. Exported for the test; the
 * SDK's own prototype is wrapped by {@link installMoonPayTrace}.
 */
export function traceTransportPrototype(proto: TraceableTransport): void {
  const marked = proto as TraceableTransport & { [TRACED]?: true }
  if (marked[TRACED] === true) {
    return
  }
  marked[TRACED] = true

  const { handleWebViewMessage, sendMessage } = proto

  proto.handleWebViewMessage = function (data: string): void {
    if (!tracingEnabled()) {
      handleWebViewMessage.call(this, data)
      return
    }
    const trace = traceEnvelope(data)
    if (trace !== undefined) {
      logger.debug('connect-rn:moonpay:frame_trace', {
        frameUrl: this.url,
        ...trace,
      })
      return
    }
    logger.debug('connect-rn:moonpay:frame_in', {
      frameUrl: this.url,
      raw: data,
    })
    handleWebViewMessage.call(this, data)
  }

  proto.sendMessage = function (message: unknown): void {
    if (tracingEnabled()) {
      logger.debug('connect-rn:moonpay:frame_out', {
        frameUrl: this.url,
        message,
      })
    }
    sendMessage.call(this, message)
  }
}

/**
 * Installed at import time, which is before the debug tag resolves — so the
 * wrappers check {@link tracingEnabled} per message instead of gating install.
 * Guarded because this runs at module scope on the deposit path: a MoonPay
 * release that drops the export must not take the flow down with it.
 */
export function installMoonPayTrace(): void {
  // Namespace access rather than a named import: a MoonPay release that drops
  // the export, or a test that mocks the module partially, then yields
  // `undefined` here instead of failing the whole module's import.
  const proto: unknown = (
    moonPaySdk as { WebViewTransport?: { prototype?: unknown } }
  ).WebViewTransport?.prototype
  if (typeof proto !== 'object' || proto === null) {
    return
  }
  traceTransportPrototype(proto as TraceableTransport)
}

// Runs inside MoonPay's page. Everything it captures leaves through
// `ReactNativeWebView.postMessage`, which the wrap above intercepts before the
// SDK's own `kind` check would discard it.
const TRACE_SCRIPT = `
;(function () {
  if (window.__funTraceInstalled) return
  window.__funTraceInstalled = true
  var MAX = 65536
  function send(kind, payload) {
    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ __funTrace: { kind: kind, at: Date.now(), payload: payload } })
      )
    } catch (e) {}
  }
  function clip(text) {
    if (typeof text !== 'string') return { body: text }
    return text.length > MAX
      ? { body: text.slice(0, MAX), truncated: true }
      : { body: text }
  }
  var nativeFetch = window.fetch
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : input && input.url
    var method = (init && init.method) || 'GET'
    var started = Date.now()
    return nativeFetch.apply(this, arguments).then(
      function (response) {
        response.clone().text().then(function (text) {
          send('fetch', {
            url: url,
            method: method,
            status: response.status,
            durationMs: Date.now() - started,
            response: clip(text)
          })
        }).catch(function () {})
        return response
      },
      function (error) {
        send('fetch_error', {
          url: url,
          method: method,
          durationMs: Date.now() - started,
          message: String(error && error.message)
        })
        throw error
      }
    )
  }
  var xhrOpen = XMLHttpRequest.prototype.open
  var xhrSend = XMLHttpRequest.prototype.send
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__funTraceMeta = { method: method, url: url }
    return xhrOpen.apply(this, arguments)
  }
  XMLHttpRequest.prototype.send = function () {
    var meta = this.__funTraceMeta
    var started = Date.now()
    if (meta) {
      this.addEventListener('loadend', function () {
        send('xhr', {
          url: meta.url,
          method: meta.method,
          status: this.status,
          durationMs: Date.now() - started,
          response: clip(this.responseText)
        })
      })
    }
    return xhrSend.apply(this, arguments)
  }
  ;['log', 'warn', 'error'].forEach(function (level) {
    var original = console[level]
    console[level] = function () {
      send('console', {
        level: level,
        args: Array.prototype.map.call(arguments, String)
      })
      return original.apply(console, arguments)
    }
  })
  window.addEventListener('error', function (event) {
    send('error', {
      message: String(event.message),
      source: event.filename,
      line: event.lineno
    })
  })
  window.addEventListener('unhandledrejection', function (event) {
    send('unhandledrejection', { reason: String(event.reason) })
  })
})()
true;
`

/** The script emits into a void unless the wrap above is listening. */
export function moonPayTraceInjection(): string | undefined {
  return tracingEnabled() ? TRACE_SCRIPT : undefined
}
